// request-guard.ts
// Wraps fetch, XHR, and sendBeacon. Emits single-object JSentinelMessage via `emit`.

import { emit } from "../core/events";
import type { RequestStartDetail, RequestEndDetail } from "../types";

const genId = () => Math.random().toString(36).slice(2);

function now() {
  return typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
}

function maskUrl(url: string): string {
  // Hide query string to avoid leaking PII into telemetry
  try {
    const u = new URL(url, location.origin);
    u.search = u.search ? "?***" : "";
    return u.toString();
  } catch {
    return url;
  }
}

function perfTransfer(url: string) {
  try {
    const items = performance.getEntriesByType("resource") as PerformanceResourceTiming[];
    for (let i = items.length - 1; i >= 0; i--)
      if (items[i].name === url) {
        return { transferSize: items[i].transferSize || undefined };
      }
  } catch {}
  return {};
}

export function installRequestGuard(): void {
  wrapFetch();
  wrapXHR();
  wrapBeacon();
}

function wrapFetch() {
  const _fetch = (window as any).fetch as typeof fetch | undefined;
  if (!_fetch) return;

  (window as any).fetch = async function (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const rawUrl = typeof input === "string" ? input : (input as any)?.url || String(input);
    const id = genId();
    const method = (init?.method || (input as any)?.method || "GET").toUpperCase();
    const t0 = now();

    const start: RequestStartDetail = {
      id,
      kind: "fetch",
      direction: "outbound",
      url: maskUrl(rawUrl),
      method,
      ts: Date.now(),
    };
    emit({ type: "request.start", data: start });

    try {
      const res = await _fetch(input as any, init);
      const t1 = now();
      const headers: Record<string, string> = {};
      try {
        res.headers.forEach((v, k) => (headers[k.toLowerCase()] = v));
      } catch {}

      const end: RequestEndDetail = {
        id,
        kind: "fetch",
        direction: "inbound",
        url: maskUrl(rawUrl),
        status: res.type === "opaque" ? "opaque" : res.status,
        ok: res.type === "opaque" ? null : res.ok,
        duration_ms: Math.round(t1 - t0),
        headers,
        size: {
          contentLength: Number(headers["content-length"]) || undefined,
          ...perfTransfer(rawUrl),
        },
        ts: Date.now(),
      };
      emit({ type: "request.end", data: end });
      return res;
    } catch (err: any) {
      const t1 = now();
      const endErr: RequestEndDetail = {
        id,
        kind: "fetch",
        direction: "inbound",
        url: maskUrl(rawUrl),
        status: null,
        ok: null,
        duration_ms: Math.round(t1 - t0),
        error: String(err?.message || err),
        ts: Date.now(),
      };
      emit({ type: "request.end", data: endErr });
      throw err;
    }
  } as typeof fetch;
}

function wrapXHR() {
  const XHR = (window as any).XMLHttpRequest;
  if (!XHR) return;
  const _open = XHR.prototype.open;
  const _send = XHR.prototype.send;

  XHR.prototype.open = function (method: string, url: string, ...rest: any[]) {
    (this as any).__js_req = {
      id: genId(),
      method: (method || "GET").toUpperCase(),
      url,
      t0: 0,
    };
    return _open.call(this, method, url, ...rest);
  };

  XHR.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
    const ctx = (this as any).__js_req;
    if (ctx) {
      ctx.t0 = now();

      const start: RequestStartDetail = {
        id: ctx.id,
        kind: "xhr",
        direction: "outbound",
        url: maskUrl(ctx.url),
        method: ctx.method,
        ts: Date.now(),
      };
      emit({ type: "request.start", data: start });

      this.addEventListener(
        "loadend",
        () => {
          const t1 = now();
          let headers: Record<string, string> | undefined;
          try {
            const raw = this.getAllResponseHeaders() || "";
            headers = {};
            raw
              .trim()
              .split(/[\r\n]+/)
              .forEach((line: string) => {
                const idx = line.indexOf(":");
                if (idx > -1)
                  headers![line.slice(0, idx).toLowerCase()] = line.slice(idx + 1).trim();
              });
          } catch {}

          const end: RequestEndDetail = {
            id: ctx.id,
            kind: "xhr",
            direction: "inbound",
            url: maskUrl(ctx.url),
            status: this.status || null,
            ok: this.status ? this.status >= 200 && this.status < 400 : null,
            duration_ms: Math.round(t1 - ctx.t0),
            headers,
            size: {
              contentLength: Number(headers?.["content-length"]) || undefined,
              ...perfTransfer(ctx.url),
            },
            error: this.status === 0 && this.readyState === 4 ? "network-error" : undefined,
            ts: Date.now(),
          };
          emit({ type: "request.end", data: end });
        },
        { once: true },
      );

      this.addEventListener(
        "error",
        () => {
          const t1 = now();
          const endErr: RequestEndDetail = {
            id: ctx.id,
            kind: "xhr",
            direction: "inbound",
            url: maskUrl(ctx.url),
            status: null,
            ok: null,
            duration_ms: Math.round(t1 - ctx.t0),
            error: "xhr-error",
            ts: Date.now(),
          };
          emit({ type: "request.end", data: endErr });
        },
        { once: true },
      );
    }
    return _send.call(this, body as any);
  };
}

function wrapBeacon() {
  const _beacon = navigator.sendBeacon?.bind(navigator);
  if (!_beacon) return;

  navigator.sendBeacon = (url: string | URL, data?: BodyInit | null) => {
    const href = String(url);
    const id = genId();
    const t0 = now();

    const start: RequestStartDetail = {
      id,
      kind: "beacon",
      direction: "outbound",
      url: maskUrl(href),
      method: "POST",
      ts: Date.now(),
    };
    emit({ type: "request.start", data: start });

    const ok = _beacon!(url, data);
    const t1 = now();

    const end: RequestEndDetail = {
      id,
      kind: "beacon",
      direction: "inbound",
      url: maskUrl(href),
      status: ok ? 204 : null,
      ok: ok ? true : null,
      duration_ms: Math.round(t1 - t0),
      ts: Date.now(),
    };
    emit({ type: "request.end", data: end });
    return ok;
  };
}
